import { HomeNextConversationDto, HomeTodoDto } from "@coaching-culture/types";
import { Button, formatRelative, PageHeader, Text } from "@coaching-culture/ui";
import { format } from "date-fns";
import { groupTodos, isCompleted } from "./todoPresentation";

const greetingFor = (d: Date) => {
  const h = d.getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
};

export type HomeHeaderProps = {
  name: string;
  todos: HomeTodoDto[] | undefined;
  next: HomeNextConversationDto | null | undefined;
  canGiveFeedback: boolean;
  canSchedule: boolean;
};

export function HomeHeader({
  name,
  todos,
  next,
  canGiveFeedback,
  canSchedule,
}: HomeHeaderProps) {
  const now = new Date();
  const firstName = name.split(" ")[0] || name;
  // The summary counts what is left to do, so items ticked off in the last
  // day — which stay on the list — don't count towards it.
  const open = todos == null ? null : todos.filter((x) => !isCompleted(x));
  const groups = open == null ? null : groupTodos(open, now);

  const parts: React.ReactNode[] = [];
  if (groups != null) {
    if (groups.overdue.length > 0) {
      parts.push(
        <Text as="b" color="danger" fontWeight={600} key="o">
          {groups.overdue.length} overdue
        </Text>,
      );
    }
    if (groups.today.length > 0) {
      parts.push(<span key="t">{groups.today.length} due today</span>);
    }
    if (parts.length === 0 && open!.length === 0) {
      parts.push(<span key="c">You're all caught up</span>);
    }
  }
  if (next != null) {
    parts.push(
      <span key="n">
        Next conversation{" "}
        {formatRelative(next.scheduledDate).replace(/^(Today|Tomorrow)/, (m) =>
          m.toLowerCase(),
        )}
      </span>,
    );
  }

  const summary =
    parts.length > 0
      ? parts.map((p, i) => (
          <span key={i}>
            {i > 0 && " · "}
            {p}
          </span>
        ))
      : undefined;

  return (
    <PageHeader
      mb={0}
      eyebrow={format(now, "EEEE d MMMM")}
      title={`${greetingFor(now)}, ${firstName}`}
      description={summary}
      actions={
        canGiveFeedback || canSchedule ? (
          <>
            {canGiveFeedback && (
              <Button to="/solutions/continuous-feedback/send" filled={false}>
                Give feedback
              </Button>
            )}
            {canSchedule && (
              <Button
                to="/solutions/performance/conversations/create"
                color="primary"
                filled
              >
                Schedule conversation
              </Button>
            )}
          </>
        ) : undefined
      }
    />
  );
}
